import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
  query,
  where,
  documentId,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { AUTH_LOGIN, DATABASE, STORAGE, FUNCTIONS } from "../Config/firebase";
import { httpsCallable } from "firebase/functions";
import FUNCTIONS_URLS from "../Config/functions";
import {
  KYC_STATUS,
  SCREENING_STATUS,
  KYC_DOC_STATUS,
  CLIENT_CLASSIFICATION,
  CONFIDENCE,
  APP_USER_STATUS,
  KYC_DOC_TYPES,
  LEGACY_DOCUMENT_TYPE_MAP,
  inferDocTypeFromPath,
  normalizeNameKey,
} from "../Utils/kycConstants";
import {
  buildComplianceChecklist,
  computeCompletenessScore,
  deriveKycStatusFromDocs,
  canApproveKyc,
  nextReviewDateFromRisk,
} from "../Utils/kycCompliance";
import { normalizeExpiryToIso, normalizeDobToIso } from "../Utils/kycIdExtraction";
import {
  buildKycFillPlan,
  summarizeFillPlan,
  documentIdsToRead,
  parseDateLoose,
  isEmptyValue,
  identityScanPlan,
  nextAutomaticKycStatus,
  alternateIdentityDocId,
  SCREENING_DOC_ID,
  CONTRACT_DOC_ID,
} from "../Utils/kycProfileFill";

const KYC_COLLECTION = "kyc_profiles";
const USERS_COLLECTION = "users";

/**
 * Persist the client's date of birth to the canonical location used by Client
 * Communications (users/{uid}.birth_date), mirrored on the KYC profile for display.
 * DOB is always ISO YYYY-MM-DD. No-op when dob is falsy.
 * Uses setDoc(merge) so it works even if the KYC profile doc is missing fields.
 */
async function persistBirthDate(userId, dob) {
  const iso = dob ? normalizeDobToIso(dob) : null;
  if (!userId || !iso) return null;
  await setDoc(
    doc(DATABASE, USERS_COLLECTION, userId),
    { birth_date: iso },
    { merge: true }
  );
  await setDoc(
    doc(DATABASE, KYC_COLLECTION, userId),
    { birth_date: iso, updated_at: serverTimestamp() },
    { merge: true }
  );
  return iso;
}

/** Public helper — save DOB from Customer View / other screens. */
export async function saveUserBirthDate(userId, birthDate) {
  return persistBirthDate(userId, birthDate);
}

export async function fetchAllKycProfiles() {
  const snap = await getDocs(collection(DATABASE, KYC_COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function mapUserDoc(d) {
  const data = d.data();
  return {
    user_id: d.id,
    doc_id: data.doc_id || d.id,
    email: (data.email || "").toLowerCase().trim(),
    full_name: data.full_name || data.name || "",
    name_key: normalizeNameKey(data.full_name || data.name || ""),
    phone: data.phone || "",
    user_type: data.user_type,
    status: data.status,
    document_number: data.document_number || "",
    document: data.document || null,
    document_back: data.document_back || null,
    document_type: data.document_type,
    document_expiry_date:
      data.document_expiry_date ||
      data.document_expiration_date ||
      data.passport_expiry_date ||
      data.id_expiry_date ||
      null,
    registered_date: data.registered_date || null,
    document_update_request: data.document_update_request || null,
  };
}

/** Fetch only users linked to KYC profiles (batched by id — faster than loading all clients). */
export async function fetchClientsByIds(userIds = []) {
  const unique = [...new Set(userIds.filter(Boolean))];
  if (!unique.length) return [];
  const users = [];
  const CHUNK = 30;
  for (let i = 0; i < unique.length; i += CHUNK) {
    const chunk = unique.slice(i, i + CHUNK);
    const q = query(collection(DATABASE, "users"), where(documentId(), "in", chunk));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => users.push(mapUserDoc(d)));
  }
  return users;
}

/** Profiles enriched with live users collection (status, document, classification). */
export async function fetchKycProfilesForCenter() {
  const profileSnap = await getDocs(collection(DATABASE, KYC_COLLECTION));
  const profileById = new Map(profileSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
  const linkedIds = [...profileById.values()].filter((p) => !p.is_orphan).map((p) => p.id);
  const users = await fetchClientsByIds(linkedIds);
  const userById = new Map(users.map((u) => [u.user_id, u]));

  const merged = [];
  for (const [id, profile] of profileById) {
    if (profile.is_orphan) {
      merged.push(profile);
      continue;
    }
    const user = userById.get(id);
    if (!user) {
      merged.push(profile);
      continue;
    }
    merged.push({
      ...profile,
      display_name: profile.display_name || user.full_name,
      email: profile.email || user.email,
      phone: profile.phone || user.phone,
      classification: user.user_type === 1 ? CLIENT_CLASSIFICATION.CORPORATE : CLIENT_CLASSIFICATION.INDIVIDUAL,
      user_type: user.user_type,
      app_user_status: user.status ?? APP_USER_STATUS.PENDING,
      users_document_url: profile.users_document_url || user.document || null,
      users_document_back_url:
        profile.users_document_back_url || user.document_back || null,
      users_document_number: profile.users_document_number || user.document_number || "",
      identity_document_number:
        profile.identity_document_number || user.document_number || profile.users_document_number || null,
      identity_expiry_date:
        profile.identity_expiry_date || profile.document_expiry_date || null,
      client_address: profile.client_address || profile.contract_summary?.client_address || null,
      document_update_request: user.document_update_request || profile.document_update_request || null,
      users_doc_id: user.doc_id || null,
    });
  }
  return merged;
}

function pickIdentityFieldsFromDocuments(documents = [], user = {}) {
  const identityDoc =
    documents.find((d) => d.id === "users_document_identity") ||
    documents.find(
      (d) =>
        ["passport", "national_id", "drivers_license"].includes(d.doc_type) &&
        d.status !== KYC_DOC_STATUS.MISSING
    );
  return {
    identity_document_number:
      identityDoc?.id_number ||
      identityDoc?.document_number ||
      user.document_number ||
      null,
    identity_expiry_date: identityDoc?.expiry_date || null,
  };
}

export async function fetchKycProfile(userId) {
  if (!userId) return null;
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function fetchKycSubcollection(userId, sub) {
  const snap = await getDocs(collection(DATABASE, KYC_COLLECTION, userId, sub));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchAllClientsForMatching() {
  const q = query(collection(DATABASE, "users"), where("role", "==", 1));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapUserDoc(d));
}

function provenanceField(value, source = {}) {
  return {
    value: value ?? null,
    confidence: source.confidence || CONFIDENCE.MEDIUM,
    source_file: source.source_file || null,
    source_method: source.source_method || "import",
    extracted_at: source.extracted_at || new Date().toISOString(),
  };
}

async function readKycFillInputs(userId, tx, { overrides = {}, isOrphan = false } = {}) {
  const read = (r) => (tx ? tx.get(r) : getDoc(r));
  const userSnap = isOrphan ? null : await read(doc(DATABASE, USERS_COLLECTION, userId));
  const user = userSnap && userSnap.exists() ? mapUserDoc(userSnap) : null;
  const profileSnap = await read(doc(DATABASE, KYC_COLLECTION, userId));
  const documents = {};
  for (const id of documentIdsToRead(user, overrides)) {
    const s = await read(doc(DATABASE, KYC_COLLECTION, userId, "documents", id));
    documents[id] = s.exists() ? s.data() : null;
  }
  const screeningSnap = await read(doc(DATABASE, KYC_COLLECTION, userId, "screening", SCREENING_DOC_ID));
  const contractSnap = await read(doc(DATABASE, KYC_COLLECTION, userId, "contracts", CONTRACT_DOC_ID));
  const metaSnap = await read(doc(DATABASE, "contract_metadata", userId));
  return {
    uid: userId,
    user,
    profile: profileSnap.exists() ? profileSnap.data() : null,
    documents,
    screening: screeningSnap.exists() ? screeningSnap.data() : null,
    contract: contractSnap.exists() ? contractSnap.data() : null,
    contractMeta: metaSnap.exists() ? metaSnap.data() : null,
  };
}

/**
 * Read-only preview of what a fill would write on kyc_profiles/{userId}.
 * Returns field NAMES only (summarizeFillPlan) plus the signature to pass to apply.
 */
export async function planKycProfileFill(userId, opts = {}) {
  const { overrides = {}, isOrphan = false, allowCreate = true, source = "kyc_fill", matchMethod } = opts;
  const allDocuments = await fetchKycSubcollection(userId, "documents").catch(() => []);
  const inputs = await readKycFillInputs(userId, null, { overrides, isOrphan });
  const plan = buildKycFillPlan({
    ...inputs,
    allDocuments,
    overrides,
    isOrphan,
    allowCreate,
    source,
    matchMethod,
    actorUid: AUTH_LOGIN.currentUser?.uid || null,
  });
  return { ...summarizeFillPlan(plan), signature: plan.signature };
}

/**
 * Fill-only write in a single transaction: every field is re-read inside the transaction
 * and written only when still empty. Never deletes, never touches users / crypto_wallets.
 * If expectedSignature is given and the data changed since the preview, throws code "plan_changed".
 */
export async function applyKycProfileFill(userId, opts = {}) {
  const {
    expectedSignature = null,
    overrides = {},
    isOrphan = false,
    allowCreate = true,
    source = "kyc_fill",
    matchMethod,
  } = opts;
  const actorUid = AUTH_LOGIN.currentUser?.uid;
  if (!actorUid) {
    const err = new Error("Not signed in.");
    err.code = "unauthenticated";
    throw err;
  }
  const allDocuments = await fetchKycSubcollection(userId, "documents").catch(() => []);
  return runTransaction(DATABASE, async (tx) => {
    const inputs = await readKycFillInputs(userId, tx, { overrides, isOrphan });
    const plan = buildKycFillPlan({
      ...inputs,
      allDocuments,
      overrides,
      isOrphan,
      allowCreate,
      source,
      matchMethod,
      actorUid,
      stamp: serverTimestamp(),
    });
    if (expectedSignature != null && plan.signature !== expectedSignature) {
      const err = new Error("KYC data changed since the preview. Run the preview again.");
      err.code = "plan_changed";
      throw err;
    }
    if (plan.totalFilled > 0) {
      const profileRef = doc(DATABASE, KYC_COLLECTION, userId);
      if (plan.createProfile) tx.set(profileRef, plan.profile.patch);
      else tx.update(profileRef, plan.profile.patch);
      for (const s of plan.subdocs) {
        const r = doc(DATABASE, KYC_COLLECTION, userId, s.collection, s.id);
        if (s.exists) tx.update(r, s.patch);
        else tx.set(r, s.patch);
      }
    }
    return summarizeFillPlan(plan);
  });
}

function inferExpiryDateFromFilename(filePath = "") {
  const lower = String(filePath || "").toLowerCase();
  if (!/(expir|expiry|valid|venc|passport|license|id|identit)/.test(lower)) return null;
  return parseDateLoose(filePath);
}

function inferNetworkFromAddress(address = "") {
  const a = String(address || "").trim();
  if (!a) return "POLYGON";
  if (/^0x[a-fA-F0-9]{40}$/.test(a)) return "POLYGON";
  if (/^T[a-zA-Z0-9]{33}$/.test(a)) return "TRON";
  if (/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,60}$/i.test(a)) return "BITCOIN";
  return "POLYGON";
}

export async function upsertKycProfile(userId, payload, actor = "admin") {
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId);
  const existing = await getDoc(refDoc);
  const base = {
    user_id: userId,
    updated_at: serverTimestamp(),
    updated_by: actor,
  };
  if (!existing.exists()) {
    await setDoc(refDoc, {
      ...base,
      ...payload,
      created_at: serverTimestamp(),
      created_by: actor,
      kyc_status: payload.kyc_status || KYC_STATUS.DRAFT,
      screening_status: payload.screening_status || SCREENING_STATUS.PENDING,
      risk_rating: payload.risk_rating || "medium",
    });
  } else {
    await updateDoc(refDoc, { ...base, ...payload });
  }
}

export async function upsertKycDocument(userId, docPayload) {
  const docId =
    docPayload.id ||
    `${docPayload.doc_type || "other"}_${(docPayload.source_file || "app").replace(/[^a-z0-9]/gi, "_").slice(0, 40)}`;
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId, "documents", docId);
  await setDoc(
    refDoc,
    {
      ...docPayload,
      doc_id: docId,
      updated_at: serverTimestamp(),
    },
    { merge: true }
  );
  return docId;
}

export async function upsertKycContract(userId, contractPayload) {
  const contractId = contractPayload.id || "primary";
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId, "contracts", contractId);
  const fields = {
    signature_date: provenanceField(
      contractPayload.signature_date,
      contractPayload
    ),
    initial_amount: provenanceField(contractPayload.initial_amount, contractPayload),
    contract_wallet: provenanceField(contractPayload.contract_wallet, contractPayload),
    fiddux_wallet_disclosed: provenanceField(
      contractPayload.fiddux_wallet_disclosed,
      contractPayload
    ),
    client_address: provenanceField(contractPayload.client_address, contractPayload),
    withdrawal_wallets: contractPayload.withdrawal_wallets || [],
    source: contractPayload.source || "import",
    updated_at: serverTimestamp(),
  };
  await setDoc(refDoc, { contract_id: contractId, ...fields }, { merge: true });
  return contractId;
}

export async function ensureDefaultScreening(userId) {
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId, "screening", "primary");
  const snap = await getDoc(refDoc);
  if (snap.exists()) return;
  await setDoc(refDoc, {
    check_id: "primary",
    status: SCREENING_STATUS.PENDING,
    pep_checked: false,
    sanctions_checked: false,
    notes: "Awaiting manual or automated screening",
    updated_at: serverTimestamp(),
  });
}

export async function updateScreening(userId, payload, actor) {
  const refDoc = doc(DATABASE, KYC_COLLECTION, userId, "screening", "primary");
  await setDoc(
    refDoc,
    {
      ...payload,
      reviewed_by: actor,
      reviewed_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    },
    { merge: true }
  );
  await refreshProfileCompliance(userId);
}

function applyDocumentExpiryRules(documents) {
  const now = Date.now();
  return documents.map((d) => {
    if (d.expiry_date) {
      const exp = new Date(d.expiry_date).getTime();
      if (!Number.isNaN(exp) && exp < now) {
        return { ...d, status: KYC_DOC_STATUS.EXPIRED };
      }
    }
    return d;
  });
}

export async function refreshProfileCompliance(userId) {
  const existing = await fetchKycProfile(userId);
  if (!existing) return;

  // Registration photo and empty identity slots go through the fill-only engine.
  // It never sets VALID, never writes null over an OCR number, and never deletes required_identity.
  await applyKycProfileFill(userId, { allowCreate: false, source: "refresh_compliance" });

  const profile = (await fetchKycProfile(userId)) || existing;
  let documents = await fetchKycSubcollection(userId, "documents");
  documents = applyDocumentExpiryRules(documents);

  const hasValidId =
    documents.some(
      (d) =>
        ["passport", "national_id", "drivers_license"].includes(d.doc_type) &&
        d.status === KYC_DOC_STATUS.VALID &&
        d.id !== "required_identity"
    ) || !!profile.users_document_url;

  if (!hasValidId) {
    const missingRef = doc(DATABASE, KYC_COLLECTION, userId, "documents", "required_identity");
    const missSnap = await getDoc(missingRef);
    if (!missSnap.exists()) {
      await setDoc(missingRef, {
        doc_type: "passport",
        status: KYC_DOC_STATUS.MISSING,
        label: "Identity document required",
        updated_at: serverTimestamp(),
      });
      documents = await fetchKycSubcollection(userId, "documents");
      documents = applyDocumentExpiryRules(documents);
    }
  }

  const screeningList = await fetchKycSubcollection(userId, "screening");
  const screening = screeningList.find((s) => s.id === "primary") || screeningList[0];
  const contracts = await fetchKycSubcollection(userId, "contracts");
  const contract = contracts.find((c) => c.id === "primary") || contracts[0];

  const contractFlat = contract
    ? {
        signature_date: contract.signature_date?.value,
        contract_wallet: contract.contract_wallet?.value,
        fiddux_wallet_disclosed: contract.fiddux_wallet_disclosed?.value,
        initial_amount: contract.initial_amount?.value,
      }
    : profile.contract_summary;

  const checklist = buildComplianceChecklist(profile, documents, screening, contractFlat);
  const completeness = computeCompletenessScore(checklist);
  const derivedStatus = deriveKycStatusFromDocs(profile, documents, screening);
  const nextReview = nextReviewDateFromRisk(profile.risk_rating || "medium");
  const identityFields = pickIdentityFieldsFromDocuments(documents, profile);

  const patch = {
    compliance_checklist: checklist,
    completeness_score: completeness,
    screening_status: screening?.status || profile.screening_status || SCREENING_STATUS.PENDING,
    next_review_at: nextReview,
    updated_at: serverTimestamp(),
  };
  const nextStatus = nextAutomaticKycStatus(profile.kyc_status, derivedStatus);
  if (nextStatus) patch.kyc_status = nextStatus;
  if (isEmptyValue(profile.identity_document_number) && !isEmptyValue(identityFields.identity_document_number)) {
    patch.identity_document_number = identityFields.identity_document_number;
  }
  if (isEmptyValue(profile.identity_expiry_date) && !isEmptyValue(identityFields.identity_expiry_date)) {
    patch.identity_expiry_date = identityFields.identity_expiry_date;
  }
  await updateDoc(doc(DATABASE, KYC_COLLECTION, userId), patch);
}

/** OCR / text extraction for identity image or PDF (Cloud Vision). */
export async function extractIdentityFromDocumentHttp(storageUrl) {
  const res = await fetch(FUNCTIONS_URLS.ExtractIdentityFromDocumentHttp, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ storageUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.details || "Identity extraction failed");
  return data;
}

const SCAN_FIELD_LABEL = {
  identity_document_number: "ID number",
  identity_expiry_date: "expiry",
  birth_date: "date of birth",
};

/** Scan identity image/PDF. Fills only empty fields. A different OCR reading is shown, not saved. */
export async function scanIdentityDocumentForUser(userId, storageUrl, _actor = "admin") {
  if (!userId || !storageUrl) throw new Error("userId and storageUrl required");

  const result = await extractIdentityFromDocumentHttp(storageUrl);
  const extracted = result.extracted || result;
  const idNumber = extracted?.document_number || null;
  const expiry = extracted?.expiry_date || null;
  const dob = extracted?.date_of_birth || null;

  if (!idNumber && !expiry && !dob) {
    return {
      document_number: null,
      expiry_date: null,
      date_of_birth: null,
      differences: [],
      applied: {},
      extracted,
      ocr_method: result.ocr_method,
      text_length: result.text_length,
      message: result.note || "No ID fields detected — enter manually and Save",
    };
  }

  const profile = await fetchKycProfile(userId);
  const docs = await fetchKycSubcollection(userId, "documents");
  const identityDoc = docs.find((d) => d.id === "users_document_identity") || null;
  const userSnap = await getDoc(doc(DATABASE, USERS_COLLECTION, userId));
  const userBirthDate = userSnap.exists() ? userSnap.data().birth_date : null;
  const plan = identityScanPlan({
    profile,
    identityDoc,
    userBirthDate,
    storageUrl,
    extracted,
  });

  await applyKycProfileFill(userId, {
    allowCreate: false,
    source: "identity_scan",
    overrides: {
      profile: plan.profileOverrides,
      documents: plan.document ? [plan.document] : [],
    },
  });

  if (plan.fillUserBirthDate && userSnap.exists()) {
    await setDoc(doc(DATABASE, USERS_COLLECTION, userId), { birth_date: plan.dob }, { merge: true });
  }

  await refreshProfileCompliance(userId);

  const applied = {
    document_number: !isEmptyValue(plan.profileOverrides.identity_document_number),
    expiry_date: !isEmptyValue(plan.profileOverrides.identity_expiry_date),
    date_of_birth: Boolean(plan.fillUserBirthDate),
  };
  const parts = [];
  if (plan.differences.length) {
    parts.push(
      `OCR differs from the saved value and was not written. ${plan.differences
        .map((d) => `${SCAN_FIELD_LABEL[d.field] || d.field}: ${d.extracted}`)
        .join(" · ")}`
    );
  }
  if (applied.document_number || applied.expiry_date || applied.date_of_birth) {
    parts.push("Empty fields were filled from the scan.");
  }

  return {
    document_number: applied.document_number ? plan.profileOverrides.identity_document_number : null,
    expiry_date: applied.expiry_date ? plan.profileOverrides.identity_expiry_date : null,
    date_of_birth: applied.date_of_birth ? plan.dob : null,
    differences: plan.differences,
    applied,
    extracted,
    ocr_method: result.ocr_method,
    text_length: result.text_length,
    message: parts.join(" ") || "OCR complete — adjust fields below and Save.",
  };
}

/** Manual entry. Writes only keys present in `fields`. Unsent keys are left as stored. */
export async function saveKycManualFields(userId, fields = {}, _actor = "admin") {
  if (!userId) throw new Error("Missing userId");

  const sent = (key) => Object.prototype.hasOwnProperty.call(fields, key);
  const idNumber = sent("identity_document_number")
    ? String(fields.identity_document_number ?? "").trim() || null
    : null;
  const expiryRaw = sent("identity_expiry_date")
    ? String(fields.identity_expiry_date ?? "").trim() || null
    : null;
  const expiry = expiryRaw ? normalizeExpiryToIso(expiryRaw) || expiryRaw : null;
  const clientAddress = sent("client_address")
    ? String(fields.client_address ?? "").trim() || null
    : null;
  const dobRaw = sent("birth_date") ? String(fields.birth_date ?? "").trim() || null : null;
  const dob = dobRaw ? normalizeDobToIso(dobRaw) || dobRaw : null;
  const same = (next, prev) => String(next || "").trim() === String(prev || "").trim();

  const profile = await fetchKycProfile(userId);
  const docs = await fetchKycSubcollection(userId, "documents");
  const identityDoc = docs.find((d) => d.id === "users_document_identity");
  const userSnap = sent("birth_date")
    ? await getDoc(doc(DATABASE, USERS_COLLECTION, userId))
    : null;
  const userBirthDate = userSnap?.exists() ? userSnap.data().birth_date : null;
  const changed = [];

  const docPatch = { id: "users_document_identity" };
  let identityChanged = false;
  if (idNumber && !same(idNumber, identityDoc?.id_number)) {
    docPatch.id_number = idNumber;
    changed.push("documents/users_document_identity.id_number");
    identityChanged = true;
  }
  if (idNumber && !same(idNumber, identityDoc?.document_number)) {
    docPatch.document_number = idNumber;
    changed.push("documents/users_document_identity.document_number");
    identityChanged = true;
  }
  if (expiry && !same(expiry, identityDoc?.expiry_date)) {
    docPatch.expiry_date = expiry;
    changed.push("documents/users_document_identity.expiry_date");
    identityChanged = true;
  }
  if (identityChanged) {
    if (identityDoc?.status !== KYC_DOC_STATUS.NEEDS_REVIEW) {
      docPatch.status = KYC_DOC_STATUS.NEEDS_REVIEW;
      changed.push("documents/users_document_identity.status");
    }
    if (!identityDoc?.doc_type) docPatch.doc_type = KYC_DOC_TYPES.PASSPORT;
    docPatch.source_method = "manual_entry";
    docPatch.entered_by = AUTH_LOGIN.currentUser?.uid || null;
    docPatch.entered_at = new Date().toISOString();
    await upsertKycDocument(userId, docPatch);
  }

  const profilePatch = {};
  if (idNumber && !same(idNumber, profile?.identity_document_number)) {
    profilePatch.identity_document_number = idNumber;
    changed.push("identity_document_number");
  }
  if (sent("users_document_number")) {
    const nextUsersNumber = String(fields.users_document_number ?? "").trim();
    if (nextUsersNumber && !same(nextUsersNumber, profile?.users_document_number)) {
      profilePatch.users_document_number = nextUsersNumber;
      changed.push("users_document_number");
    }
  }
  if (expiry && !same(expiry, profile?.identity_expiry_date)) {
    profilePatch.identity_expiry_date = expiry;
    changed.push("identity_expiry_date");
  }
  if (sent("document_expiry_date")) {
    const rawDocExpiry = String(fields.document_expiry_date ?? "").trim();
    const nextDocExpiry = rawDocExpiry ? normalizeExpiryToIso(rawDocExpiry) || rawDocExpiry : null;
    if (nextDocExpiry && !same(nextDocExpiry, profile?.document_expiry_date)) {
      profilePatch.document_expiry_date = nextDocExpiry;
      changed.push("document_expiry_date");
    }
  }
  const addressChanged = Boolean(clientAddress) && !same(clientAddress, profile?.client_address);
  if (addressChanged) {
    profilePatch.client_address = clientAddress;
    profilePatch.contract_summary = { ...(profile?.contract_summary || {}), client_address: clientAddress };
    changed.push("client_address");
  }
  if (Object.keys(profilePatch).length) {
    profilePatch.updated_at = serverTimestamp();
    await updateDoc(doc(DATABASE, KYC_COLLECTION, userId), profilePatch);
  }

  if (sent("birth_date") && dob && (!same(dob, profile?.birth_date) || !same(dob, userBirthDate))) {
    await persistBirthDate(userId, dob);
    changed.push("birth_date");
  }

  if (addressChanged) {
    const contracts = await fetchKycSubcollection(userId, "contracts");
    const primary = contracts.find((c) => c.id === "primary") || contracts[0];
    const contractId = primary?.id || "primary";
    await setDoc(
      doc(DATABASE, KYC_COLLECTION, userId, "contracts", contractId),
      {
        contract_id: contractId,
        client_address: provenanceField(clientAddress, { source_method: "manual_entry" }),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
  }

  const fieldNames = [...new Set(changed)];
  if (!fieldNames.length) {
    return {
      saved: false,
      identity_document_number: profile?.identity_document_number || null,
      identity_expiry_date: profile?.identity_expiry_date || null,
      birth_date: profile?.birth_date || null,
      client_address: profile?.client_address || null,
      fields: [],
    };
  }

  await refreshProfileCompliance(userId);
  await updateDoc(doc(DATABASE, KYC_COLLECTION, userId), {
    kyc_last_fill: {
      by_uid: AUTH_LOGIN.currentUser?.uid || null,
      at: serverTimestamp(),
      source: "manual_entry",
      fields: fieldNames,
    },
    updated_at: serverTimestamp(),
  });
  return {
    saved: true,
    identity_document_number: profilePatch.identity_document_number || profile?.identity_document_number || null,
    identity_expiry_date: profilePatch.identity_expiry_date || profile?.identity_expiry_date || null,
    birth_date: dob && changed.includes("birth_date") ? dob : profile?.birth_date || null,
    client_address: addressChanged ? clientAddress : profile?.client_address || null,
    fields: fieldNames,
  };
}

export async function setKycStatus(userId, status, actor, reason = "") {
  const approval = canApproveKyc(
    await fetchKycProfile(userId),
    await fetchKycSubcollection(userId, "documents"),
    (await fetchKycSubcollection(userId, "screening"))[0],
    (await fetchKycSubcollection(userId, "contracts"))[0]
  );
  if (status === KYC_STATUS.APPROVED && !approval.allowed) {
    throw new Error(
      `Cannot approve: ${approval.blockers.map((b) => b.label).join("; ")}`
    );
  }
  await updateDoc(doc(DATABASE, KYC_COLLECTION, userId), {
    kyc_status: status,
    status_reason: reason,
    status_changed_by: actor,
    status_changed_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

export async function uploadKycEvidence(userId, file, docType, label) {
  if (!userId || !file) throw new Error("userId and file required");
  const ext = file.name.split(".").pop() || "bin";
  const storagePath = `KYC/${userId}/${docType}_${Date.now()}.${ext}`;
  const storageRef = ref(STORAGE, storagePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await upsertKycDocument(userId, {
    doc_type: docType,
    status: KYC_DOC_STATUS.NEEDS_REVIEW,
    storage_url: url,
    storage_path: storagePath,
    label: label || file.name,
    source_file: file.name,
    uploaded_at: new Date().toISOString(),
  });
  await refreshProfileCompliance(userId);
  return url;
}

/**
 * Store a new identity photo as its own KYC document.
 * Does not replace users_document_identity, users_document_url or users.document.
 * The previous OCR stays on the previous photo.
 */
export async function uploadIdentityDocumentForUser(userId, file, _actor = "admin") {
  if (!userId || !file) throw new Error("userId and file required");
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const stamp = Date.now();
  const kycPath = `KYC/${userId}/identity_${stamp}.${ext}`;

  await uploadBytes(ref(STORAGE, kycPath), file);
  const url = await getDownloadURL(ref(STORAGE, kycPath));

  const profile = await fetchKycProfile(userId);
  const docs = await fetchKycSubcollection(userId, "documents");
  const identityDoc = docs.find((d) => d.id === "users_document_identity");
  const hasStoredIdentity =
    !isEmptyValue(identityDoc?.storage_url) ||
    !isEmptyValue(identityDoc?.id_number) ||
    !isEmptyValue(profile?.users_document_url);
  const docId = hasStoredIdentity ? alternateIdentityDocId(url) : "users_document_identity";

  await upsertKycDocument(userId, {
    id: docId,
    doc_type: identityDoc?.doc_type || KYC_DOC_TYPES.PASSPORT,
    status: KYC_DOC_STATUS.NEEDS_REVIEW,
    storage_url: url,
    storage_path: kycPath,
    label: "Identity document",
    source_method: "admin_upload",
    uploaded_at: new Date().toISOString(),
    uploaded_by: AUTH_LOGIN.currentUser?.uid || null,
  });

  if (!hasStoredIdentity && isEmptyValue(profile?.users_document_url)) {
    await setDoc(
      doc(DATABASE, KYC_COLLECTION, userId),
      { users_document_url: url, updated_at: serverTimestamp() },
      { merge: true }
    );
  }

  await refreshProfileCompliance(userId);
  await updateDoc(doc(DATABASE, KYC_COLLECTION, userId), {
    kyc_last_fill: {
      by_uid: AUTH_LOGIN.currentUser?.uid || null,
      at: serverTimestamp(),
      source: "identity_upload",
      fields: [`documents/${docId}.storage_url`],
    },
    updated_at: serverTimestamp(),
  });
  return { url, storage_path: kycPath, doc_id: docId };
}

/** Match folder client entry to Firebase user */
export function matchClientToUser(folderClient, users) {
  const email = (folderClient.email || "").toLowerCase().trim();
  if (email) {
    const byEmail = users.find((u) => u.email === email);
    if (byEmail) return { user: byEmail, method: "email", confidence: CONFIDENCE.HIGH };
  }
  const folderKey = normalizeNameKey(folderClient.display_name || folderClient.folder_name || "");
  if (folderKey) {
    const candidates = users.filter((u) => {
      const uk = u.name_key;
      return uk === folderKey || uk.includes(folderKey) || folderKey.includes(uk);
    });
    if (candidates.length === 1) {
      return { user: candidates[0], method: "name", confidence: CONFIDENCE.MEDIUM };
    }
  }
  return { user: null, method: "unmatched", confidence: CONFIDENCE.LOW };
}

/** Fill-only import. Dynamic import avoids a circular load with kycBulkActions. */
export async function importScannedDataset(dataset) {
  const { runImportScannedDataset } = await import("./kycBulkActions");
  return runImportScannedDataset(dataset);
}

export async function runWalletReconciliationHttp(userId = null) {
  const url = FUNCTIONS_URLS.KycReconciliationHttp;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.details || "Reconciliation failed");
  return data;
}

export async function loadWalletReconciliationRows(userId) {
  return fetchKycSubcollection(userId, "wallet_reconciliation");
}

export async function bulkSyncAllUsersFromFirestore() {
  const { runSyncAllFirebaseClients } = await import("./kycBulkActions");
  return runSyncAllFirebaseClients();
}

/** Fill-only re-link of users.document. Never overwrites a filled identity document. */
export async function refreshAllUserDocumentsInKyc() {
  const { runRefreshRegistrationPhotos } = await import("./kycBulkActions");
  return runRefreshRegistrationPhotos();
}

export async function importContractWalletToWalletManagement(userId, actor = "admin") {
  if (!userId) throw new Error("Missing userId");

  const profile = await fetchKycProfile(userId);
  const contracts = await fetchKycSubcollection(userId, "contracts");
  const primaryContract = contracts.find((c) => c.id === "primary") || contracts[0] || null;
  const contractWallet =
    primaryContract?.contract_wallet?.value || profile?.contract_summary?.contract_wallet || null;

  if (!contractWallet) {
    throw new Error("No contract wallet found for this client");
  }

  const walletsSnap = await getDocs(collection(DATABASE, "users", userId, "crypto_wallets"));
  const wallets = walletsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const normalizedIncoming = String(contractWallet).trim().toLowerCase();
  const existing = wallets.find(
    (w) => String(w.address || "").trim().toLowerCase() === normalizedIncoming
  );
  if (existing) {
    return { imported: false, reason: "already_exists", walletId: existing.id };
  }

  const existingIds = wallets
    .map((w) => Number.parseInt(String(w.id || ""), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  const nextId = String(existingIds.length ? Math.max(...existingIds) + 1 : 1);
  const activeWithdrawalCount = wallets.filter(
    (w) => w.wallet_type === "withdrawal" && w.is_active !== false
  ).length;

  await setDoc(doc(DATABASE, "users", userId, "crypto_wallets", nextId), {
    id: nextId,
    address: String(contractWallet).trim(),
    label: "Imported from KYC contract",
    asset: "USDT",
    network: inferNetworkFromAddress(contractWallet),
    wallet_type: "withdrawal",
    is_active: true,
    is_default: activeWithdrawalCount === 0,
    added_date: serverTimestamp(),
    added_by: actor,
    auto_pay_eligible_from: Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    deactivated_date: null,
    deactivated_by: null,
    source: "kyc_contract_import",
    source_reference: {
      kyc_profile_id: userId,
      contract_id: primaryContract?.id || "primary",
    },
    edit_history: [],
  });

  await runWalletReconciliationHttp(userId);
  return { imported: true, walletId: nextId, address: contractWallet };
}

/** Admin: ask a client to send a new ID in the Fiddux app. */
export async function requestKycDocumentUpdate(userId, { reason, internalNote } = {}) {
  try {
    const fn = httpsCallable(FUNCTIONS, "requestKycDocumentUpdate");
    const res = await fn({ userId, reason, internalNote: internalNote || "" });
    return res.data;
  } catch (e) {
    throw new Error(e?.message || "Failed to request document update");
  }
}

/** Admin: approve or reject a submitted identification document. */
export async function reviewKycDocumentUpdate(userId, { action, reviewNote } = {}) {
  try {
    const fn = httpsCallable(FUNCTIONS, "reviewKycDocumentUpdate");
    const res = await fn({ userId, action, reviewNote: reviewNote || "" });
    return res.data;
  } catch (e) {
    throw new Error(e?.message || "Failed to review document");
  }
}
